/*! Ardor Printing SEG display 3D preview v1.0.0 (window.ArdorSeg3D). View only.
 * Needs global THREE (r147) + THREE.OrbitControls: the theme's ardor-stand-three.js and ardor-stand-three-orbit.js. If they
 * are not loaded yet, create() returns an engine that loads them when the viewer is needed (lazy), then builds itself.
 * Units are inches, y up, the floor is y = 0, the front faces +z. Each face texture is the WHOLE print page as printed; only
 * the visible area shows (the silicone edge band goes into the frame). Modelling numbers not published by the supplier
 * (profile depths, feet, corner radius) are Ardor's. */
(function (root) {
"use strict";

var FOV = 28, EL_MAX = 1.45, EL_MIN = 0.02, DEFAULT_BG = "#f4f6f5", PI = Math.PI,
  FILL_HOME = 0.88, FILL_NEAR = 0.97, FILL_FAR = 0.45, VERSION = "1.0.0",
  DOC = root.document, ME = DOC && DOC.currentScript;
// camera views: [azimuth (0 = front, +PI/2 = right side), elevation]
var VIEWS = {
  home: [0.5, 0.14], front: [0, 0.06], back: [PI, 0.06], side: [1.05, 0.12],
  left: [-PI / 2, 0.06], right: [PI / 2, 0.06], top: [0, 1.4]
};
var PREFIX = { NL: "nonlit", BL: "backlit", BP: "popup", WM: "wall", CF: "fabric" },
  FAMS = { nl3: "nonlit", nl10: "nonlit", nl20: "nonlit", bl10: "backlit", bp8: "popup", bp10: "popup", wm: "wall", cf: "fabric",
    nonlit: "nonlit", backlit: "backlit", popup: "popup", wall: "wall", fabric: "fabric", "seg-display": "nonlit",
    "seg-lightbox": "backlit", "seg-popup": "popup", "seg-wall": "wall", "seg-generic": "fabric", "seg-fabric": "fabric" },
  // supplier display size (w, h, d) and print page (W, H) per key, used when the designer sends no geometry
  DISP = { "NL-3FT": [33.45, 78.75, 18, 34.25, 79.53], "NL-10FT": [118.1, 88.6, 18, 118.9, 89.37], "NL-20FT": [236.2, 88.6, 18, 237, 89.37],
    "BL-10FT": [118.1, 88.6, 17, 118.9, 89.37], "BP-8FT": [87.8, 87.8, 15, 117.56, 88.58], "BP-10FT": [117.3, 87.8, 15, 147.09, 88.58] },
  RET = 14.49, // popup side return width on the wrap page (supplier template)
  // modelling defaults (inches, ours); a passed hardware.model overrides them
  MODEL = {
    nonlit: { prof: 1.6, bar: 1.4, rim: 0.2, footL: 18, footW: 2.4, footH: 1.1, footIn: 2.4, bay: 30 },
    backlit: { prof: 4.5, bar: 1.2, rim: 0.25, footL: 17, footW: 2.6, footH: 1.1, footIn: 3, bay: 40 },
    popup: { radius: 1.6, lift: 1.2, d: 15 },
    wall: { depth: 1, rim: 0.12, gap: 0.35, hang: 60 },
    fabric: { thick: 0.06, keder: 0.43, kederT: 0.12, lift: 0.8 }
  };

function hasThree() {
  var T = root.THREE;
  return !!(T && T.WebGLRenderer && T.OrbitControls && T.BufferGeometry && T.MeshLambertMaterial && T.MeshPhongMaterial &&
    T.MeshBasicMaterial && T.CanvasTexture && T.CylinderGeometry && T.BoxGeometry && T.PlaneGeometry && T.Box3);
}

/* ---------- input helpers (lenient: designer geometry, the SEG specs entry or option text) ---------- */
function num(v, lo, hi) {
  var n = Number(v);
  return v !== null && v !== "" && v !== true && v !== false && isFinite(n) && n >= lo && n <= hi ? n : null;
}
function str(v) { return v === null || v === undefined || typeof v === "object" ? "" : String(v).toLowerCase().replace(/^\s+|\s+$/g, ""); }
function keyOf(v) { return typeof v === "string" ? v.replace(/^\s+|\s+$/g, "") : ""; }
function obj(v) { return v && typeof v === "object" ? v : {}; }
function lst(v) { return v && typeof v === "object" && typeof v.length === "number" ? v : []; }
function bgColor(v) { return typeof v === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim()) ? v.trim() : DEFAULT_BG; }
function arr(v, n) {
  if (!v || typeof v !== "object" || typeof v.length !== "number" || v.length < n) return null;
  var o = [], i, x;
  for (i = 0; i < n; i++) { x = Number(v[i]); if (v[i] === null || v[i] === "" || !isFinite(x)) return null; o.push(x); }
  return o;
}
function rectOf(h) { return h && typeof h === "object" ? arr(h.rect, 4) || arr(h.r, 4) || arr(h, 4) : null; }
function inter(a, b) {
  var x0 = Math.max(a[0], b[0]), y0 = Math.max(a[1], b[1]), x1 = Math.min(a[0] + a[2], b[0] + b[2]), y1 = Math.min(a[1] + a[3], b[1] + b[3]);
  return x1 - x0 > 0.02 && y1 - y0 > 0.02 ? [x0, y0, x1 - x0, y1 - y0] : null;
}
function sizeOf(s) { var m = /(\d+(?:\.\d+)?)\s*["”]?\s*x\s*(\d+(?:\.\d+)?)/i.exec(s || ""); return m ? [+m[1], +m[2]] : null; }
function sidesOf(v) { var s = str(v); return v === 2 || /^2$|double|2[- ]?s|two/.test(s) ? 2 : v === 1 || /^1$|single|1[- ]?s|one/.test(s) ? 1 : null; }
function catOf(v, K) {
  var s = str(v);
  if (!s) return null;
  if (FAMS[s]) return FAMS[s];
  if (/fabric only|custom seg fabric|seg fabric/.test(s)) return "fabric";
  if (/wall/.test(s)) return "wall";
  if (/pop-?\s?up/.test(s)) return "popup";
  if (/backlit|back-lit|lightbox|light box/.test(s)) return "backlit";
  if (/display|non-?\s?lit|seg/.test(s)) return "nonlit";
  return null;
}
// hidden strips that overlap the visible rect along a whole edge are trimmed off it
function trimVis(v, hid) {
  v = v.slice();
  for (var i = 0; i < hid.length && i < 40; i++) {
    var r = rectOf(hid[i]);
    if (!r || r[2] <= 0 || r[3] <= 0) continue;
    var x1 = v[0] + v[2], y1 = v[1] + v[3], rx = r[0] + r[2], ry = r[1] + r[3];
    if (r[0] <= v[0] + 0.05 && rx >= x1 - 0.05) {
      if (r[1] <= v[1] + 0.05 && ry > v[1] && ry < y1 - 1) { v[3] = y1 - ry; v[1] = ry; }
      else if (ry >= y1 - 0.05 && r[1] < y1 && r[1] > v[1] + 1) v[3] = r[1] - v[1];
    } else if (r[1] <= v[1] + 0.05 && ry >= y1 - 0.05) {
      if (r[0] <= v[0] + 0.05 && rx > v[0] && rx < x1 - 1) { v[2] = x1 - rx; v[0] = rx; }
      else if (rx >= x1 - 0.05 && r[0] < x1 && r[0] > v[0] + 1) v[2] = r[0] - v[0];
    }
  }
  return v;
}
// one printed page: {tpl:{w,h}, visible, hidden} (designer) or {W, H, vis, hidden:[{r}]} (specs); band = default edge band
function page(p, def, band) {
  p = obj(p);
  var t = obj(p.tpl), w = num(t.w, 1, 600) || num(p.W, 1, 600), h = num(t.h, 1, 600) || num(p.H, 1, 600), tpl, v, f = [], i, x;
  tpl = w && h ? { w: w, h: h } : { w: def[0], h: def[1] };
  v = arr(p.visible, 4) || arr(p.vis, 4);
  v = v ? inter(v, [0, 0, tpl.w, tpl.h]) : null;
  if (!v || v[2] < 1 || v[3] < 1) v = band ? [band[0], band[1], tpl.w - 2 * band[0], tpl.h - 2 * band[1]] : [0, 0, tpl.w, tpl.h];
  v = trimVis(v, lst(p.hidden));
  var fl = lst(p.folds);
  for (i = 0; i < fl.length && i < 4; i++) { x = num(typeof fl[i] === "object" ? fl[i] && fl[i].x : fl[i], 0, tpl.w); if (x !== null) f.push(x); }
  return { tpl: tpl, vis: v, folds: f, from: arr(p.fromFront, 4) };
}
function normalize(o) {
  var key = keyOf(o.key), K = key.toUpperCase(), hw = obj(o.hardware || o.hw), md = obj(hw.model),
    cat = catOf(o.cat, K) || catOf(o.family, K) || catOf(hw.kind, K) || PREFIX[K.slice(0, 2)] || catOf(o.name, K) || "nonlit",
    D = DISP[K] || null, base = MODEL[cat], m = {}, kk, x, sp, P, B = null, ks, fl, rot = 0, dw, dh, dd, lit;
  for (kk in base) if (base.hasOwnProperty(kk)) { x = num(md[kk], 0, 600); m[kk] = x !== null ? x : base[kk]; }
  m.color = typeof md.frameColor === "string" ? md.frameColor : cat === "wall" ? "#111111" : "#c9ccd1";
  m.feet = num(md.feet, 0, 6);
  m.centre = md.centreSupport === true || (md.centreSupport === undefined && K === "NL-20FT");
  m.single = md.singlePanel === true || (md.singlePanel === undefined && K === "NL-3FT");
  sp = o.sign && typeof o.sign === "object" ? o.sign : o.display && typeof o.display === "object" ? o.display :
    o.front && typeof o.front === "object" ? o.front : o;
  x = obj(o.custom);
  ks = sizeOf(K.replace(/^[A-Z]+-/, "")) || sizeOf(o.size) || [num(x.w, 1, 600) || num(o.cw, 1, 600) || 96, num(x.h, 1, 600) || num(o.ch, 1, 600) || 48];
  if (cat === "wall" || cat === "fabric") {
    x = cat === "wall" ? 0.44 : 0;
    P = page(sp, ks, [x, x]);
  } else P = page(sp, D ? [D[3], D[4]] : cat === "popup" ? [117.56, 88.58] : [118.9, 89.37], [0.4, 0.39]);
  var faces = num(o.faces, 1, 2) || sidesOf(o.sides) || (/-2S(?![A-Z0-9])/.test(K) ? 2 : null) ||
    (sp === o.sign || sp === o.display ? num(sp.faces, 1, 2) || sidesOf(sp.sides) : null) || 1;
  if (cat === "wall" || cat === "fabric") faces = 1;
  faces = Math.round(faces);
  dd = num(hw.d, 0.5, 60);
  if (cat === "popup") {
    fl = P.folds.length >= 2 ? [Math.min(P.folds[0], P.folds[1]), Math.max(P.folds[0], P.folds[1])] : [RET, P.tpl.w - RET];
    if (fl[0] <= P.vis[0] + 1 || fl[1] >= P.vis[0] + P.vis[2] - 1) fl = [P.tpl.w * 0.12, P.tpl.w * 0.88];
    P.folds = fl;
    var bo = o.back && typeof o.back === "object" ? o.back : sp.back && typeof sp.back === "object" ? sp.back : null,
      ey = P.vis[1], ex = Math.min(ey, 1);
    B = page(bo, [fl[1] - fl[0], P.tpl.h], [ex, ey]);
    B.from = B.from || [fl[0], 0, fl[1] - fl[0], P.tpl.h];
    dw = num(hw.w, 20, 400) || (D ? D[0] : fl[1] - fl[0] - 0.8);
    dh = num(hw.h, 20, 200) || (D ? D[1] : P.vis[3]);
    dd = num(md.d, 4, 40) || dd || 15;
  } else if (cat === "fabric") {
    dw = P.tpl.w; dh = P.tpl.h;
  } else {
    dw = P.vis[2]; dh = P.vis[3];
    if (cat === "wall") {
      var orient = str(o.orient || o.orientation), turn = str(o.turn);
      if ((orient === "landscape" || orient === "portrait") && Math.abs(P.tpl.w - P.tpl.h) > 0.05 &&
        (orient === "landscape") !== (P.tpl.w > P.tpl.h)) { rot = /ccw|counter|left|-90/.test(turn) ? -1 : 1; dw = P.vis[3]; dh = P.vis[2]; }
    }
  }
  // backlit custom fabric: "lights on" shows the graphic lit from behind (in a lit frame the customer has)
  lit = cat === "backlit" || cat === "popup" || (cat === "fabric" && (hw.lit === true || /backlit/.test(str(o.graphic))));
  if (cat === "wall") dd = m.depth; else if (cat === "fabric") dd = m.thick;
  return { key: key, cat: cat, P: P, B: B, faces: faces, rot: rot, dw: dw, dh: dh, dd: dd || (D ? D[2] : 18), m: m, lit: lit,
    order: str(o.order).toUpperCase() };
}
function geoKey(C) {
  return JSON.stringify([C.cat, C.P.tpl, C.P.vis, C.P.folds, C.B ? [C.B.tpl, C.B.vis] : 0, C.faces, C.rot, C.dw, C.dh, C.dd, C.m, C.lit]);
}
function rng(seed) { return function () { seed = seed * 16807 % 2147483647; return seed / 2147483647; }; }

/* ---------- lazy loading of the two theme libraries ---------- */
var libP = null;
function libUrls(opts) {
  var a = ME && ME.getAttribute ? ME : null, src = a && a.src ? a.src : "", base = src.replace(/[?#].*$/, ""),
    th = opts.three || (a && a.getAttribute("data-three")) || (base ? base.replace(/ardor-seg3d(\.min)?\.js$/, "ardor-stand-three.js") : ""),
    or = opts.orbit || (a && a.getAttribute("data-orbit")) || (base ? base.replace(/ardor-seg3d(\.min)?\.js$/, "ardor-stand-three-orbit.js") : "");
  return th && or && th !== base && or !== base ? [th, or] : null;
}
function addScript(url) {
  return new Promise(function (ok, no) {
    var s = DOC.createElement("script");
    s.src = url; s.async = false;
    s.onload = function () { ok(); }; s.onerror = function () { no(new Error("load " + url)); };
    DOC.head.appendChild(s);
  });
}
function loadLibs(urls) {
  if (hasThree()) return Promise.resolve();
  if (!libP) {
    if (!urls || typeof Promise === "undefined") return Promise.reject(new Error("no libraries"));
    libP = (root.THREE && root.THREE.WebGLRenderer ? Promise.resolve() : addScript(urls[0]))
      .then(function () { return root.THREE && root.THREE.OrbitControls ? null : addScript(urls[1]); })
      .then(function () { if (!hasThree()) throw new Error("three missing"); });
    libP.catch(function () { libP = null; });
  }
  return libP;
}
function webglOk() {
  try {
    var c = DOC.createElement("canvas"), g = c.getContext("webgl2") || c.getContext("webgl"), x = g && g.getExtension("WEBGL_lose_context");
    if (x) x.loseContext();
    return !!g;
  } catch (e) { return false; }
}

function create(host, opts) {
  opts = opts || {};
  if (!host) return null;
  if (hasThree()) return buildSafe(host, opts);
  var urls = libUrls(opts);
  if (!urls || typeof Promise === "undefined" || !webglOk()) return null;
  return lazy(host, opts, urls);
}
function buildSafe(host, opts) {
  try { return build(host, opts); } catch (e) {
    try { // clean up a half-built viewer so the page can fall back to 2D
      var cs = host.querySelectorAll("canvas[data-ardor-seg3d='init']"), i, gl, ext;
      for (i = 0; i < cs.length; i++) {
        gl = cs[i].getContext("webgl2") || cs[i].getContext("webgl");
        ext = gl && gl.getExtension("WEBGL_lose_context");
        if (ext) ext.loseContext();
        if (cs[i].parentNode) cs[i].parentNode.removeChild(cs[i]);
      }
      var ui = host.querySelector("[data-ardor-seg3d-ui]");
      if (ui && ui.parentNode) ui.parentNode.removeChild(ui);
    } catch (e2) { }
    return null;
  }
}
// an engine that queues calls until THREE is loaded (when the viewer is near the screen and the page has loaded, or on
// the first touch), then builds and replays them. If loading fails it fires "ardorseg3d:fail" on the host.
function lazy(host, opts, urls) {
  var win = DOC.defaultView || root, eng = null, dead = false, sets = [], lastKey = null, faceQ = {}, lightQ = null, viewQ = null, started = false,
    io = null, note = DOC.createElement("div");
  note.setAttribute("data-ardor-seg3d-wait", "");
  note.style.cssText = "position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);text-align:center;font:600 13px/1.4 Helvetica,Arial,sans-serif;color:#8a9097;pointer-events:none;";
  note.textContent = "Loading the 3D preview…";
  try { if (win.getComputedStyle && win.getComputedStyle(host).position === "static") host.style.position = "relative"; } catch (e0) { }
  host.appendChild(note);
  function drop() { if (note.parentNode) note.parentNode.removeChild(note); }
  function fail() {
    drop();
    try { host.dispatchEvent(new win.CustomEvent("ardorseg3d:fail", { bubbles: true })); } catch (e) { }
    if (typeof opts.onFail === "function") try { opts.onFail(); } catch (e2) { }
  }
  function start() {
    if (started || dead) return;
    started = true;
    stopWatch();
    loadLibs(urls).then(function () {
      if (dead) return;
      eng = buildSafe(host, opts);
      drop();
      if (!eng) { fail(); return; }
      for (var i = 0; i < sets.length; i++) eng.set(sets[i]);
      sets = [];
      for (var f in faceQ) if (faceQ.hasOwnProperty(f)) eng.setFace(f, faceQ[f]);
      faceQ = {};
      if (lightQ !== null) eng.lights(lightQ);
      eng.refresh();
      if (viewQ) eng.view(viewQ);
      if (typeof opts.onReady === "function") try { opts.onReady(eng); } catch (e) { }
    }, function () { if (!dead) fail(); });
  }
  var ev = ["pointerdown", "touchstart", "wheel", "keydown"], loaded = DOC.readyState === "complete", seen = !win.IntersectionObserver;
  function go() { start(); }
  function maybe() {
    if (!seen || !loaded || started) return;
    if (win.requestIdleCallback) win.requestIdleCallback(go, { timeout: 2000 }); else win.setTimeout(go, 600);
  }
  function onLoad() { loaded = true; maybe(); }
  function stopWatch() {
    for (var i = 0; i < ev.length; i++) host.removeEventListener(ev[i], go);
    win.removeEventListener("load", onLoad);
    if (io) try { io.disconnect(); } catch (e) { }
  }
  for (var i = 0; i < ev.length; i++) host.addEventListener(ev[i], go, { passive: true });
  if (opts.lazy === false) start();
  else {
    if (!loaded) win.addEventListener("load", onLoad);
    if (!seen) {
      io = new win.IntersectionObserver(function (en) {
        for (var j = 0; j < en.length; j++) if (en[j].isIntersecting) { seen = true; io.disconnect(); maybe(); }
      }, { rootMargin: "300px" });
      io.observe(host);
    }
    maybe();
  }
  var fw = function (name) { return function () { return eng ? eng[name].apply(eng, arguments) : name === "stats" ? { lazy: true, started: started } : undefined; }; };
  return {
    lazy: true,
    set: function (o) {
      if (dead || !o || typeof o !== "object") return;
      if (eng) return eng.set(o);
      if (o.key !== undefined && keyOf(o.key) !== lastKey) { sets = []; lastKey = keyOf(o.key); } // a new key forgets the rest anyway
      sets.push(o);
      if (sets.length > 60) sets = sets.slice(-60);
      if (o.lights !== undefined) lightQ = o.lights !== false;
    },
    setFace: function (f, cv) { if (eng) eng.setFace(f, cv); else faceQ[f] = cv; },
    refresh: function () { if (eng) eng.refresh(); },
    view: function (n) { if (eng) eng.view(n); else { viewQ = n; start(); } },
    zoom: fw("zoom"), resize: fw("resize"), stats: fw("stats"),
    lights: function (on) { if (eng) return eng.lights(on); if (on !== undefined) lightQ = on !== false; return lightQ !== false; },
    lightsOn: function () { return eng ? eng.lightsOn() : lightQ !== false; },
    snapshot: function (w, h, v) { if (eng) return eng.snapshot(w, h, v); start(); return null; },
    dispose: function () { dead = true; stopWatch(); drop(); if (eng) eng.dispose(); eng = null; },
    ready: function () { return !!eng; }
  };
}

/* ---------- viewer ---------- */
function build(host, opts) {
  var THREE = root.THREE, doc = host.ownerDocument || DOC, win = doc.defaultView || root,
    bg = bgColor(opts.background), canvas = doc.createElement("canvas");
  canvas.setAttribute("aria-label", "3D preview of your SEG display. Drag to turn it.");
  canvas.setAttribute("data-ardor-seg3d", "init");
  canvas.style.cssText = "position:absolute;top:0;left:0;right:0;bottom:0;inset:0;width:100%;height:100%;display:block;touch-action:none;outline:none;cursor:grab;";
  var attrs = { alpha: false, antialias: true, depth: true, stencil: false, premultipliedAlpha: true, preserveDrawingBuffer: false },
    gl = null;
  try { gl = canvas.getContext("webgl2", attrs); } catch (e1) { gl = null; }
  if (!gl) try { gl = canvas.getContext("webgl", attrs) || canvas.getContext("experimental-webgl", attrs); } catch (e2) { gl = null; }
  if (!gl) return null;
  var renderer = new THREE.WebGLRenderer({ canvas: canvas, context: gl, antialias: true, alpha: false, stencil: false, preserveDrawingBuffer: false });
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.toneMapping = THREE.NoToneMapping;
  try { if (win.getComputedStyle && win.getComputedStyle(host).position === "static") host.style.position = "relative"; } catch (e3) { }
  host.appendChild(canvas);

  var maxAniso = 1;
  try { maxAniso = renderer.capabilities.getMaxAnisotropy() || 1; } catch (e4) { maxAniso = 1; }
  var canMip = !!renderer.capabilities.isWebGL2, scene = new THREE.Scene(),
    camera = new THREE.PerspectiveCamera(FOV, 1, 1, 6000), disposed = false;
  scene.add(camera);

  function lin(hex) { var c = new THREE.Color(hex); c.convertSRGBToLinear(); return c; }
  var bgDay = new THREE.Color(bg), bgNight = new THREE.Color(bg).lerp(new THREE.Color("#1c1f24"), 0.86);
  // lights: soft sky/ground fill, a light that follows the camera (so every side reads), a fixed key and back fill
  var hemi = new THREE.HemisphereLight(lin("#ffffff"), lin("#c9c4ba"), 0.46);
  hemi.position.set(0, 1, 0);
  scene.add(hemi);
  var head = new THREE.DirectionalLight(0xffffff, 0.34);
  head.position.set(-0.3, 0.5, 1);
  camera.add(head); camera.add(head.target);
  var key = new THREE.DirectionalLight(0xffffff, 0.36), fill = new THREE.DirectionalLight(0xffffff, 0.26);
  key.position.set(-0.45, 0.55, 0.7); fill.position.set(0.4, 0.5, -0.75);
  scene.add(key); scene.add(fill);
  var LAMPS = [[hemi, 0.46], [head, 0.34], [key, 0.36], [fill, 0.26]];

  /* materials (kept for the life of the viewer) */
  // print material: uv 0..1 spans the visible area; transparent print pixels read as white fabric. uLit = 1 shows the
  // print at full brightness whatever the room light (the LEDs behind it); uMix fades it to the fabric's back colour.
  var LIT = { value: 0 }, OFF = { value: 0 }, mixCol = lin("#dcdcd8");
  function artMat(tag, lit, mix) {
    var m = new THREE.MeshLambertMaterial({ color: 0xffffff }), mx = { value: mix || 0 }, mc = { value: mixCol };
    m.onBeforeCompile = function (sh) {
      sh.uniforms.uLit = lit ? LIT : OFF; sh.uniforms.uMix = mx; sh.uniforms.uMixCol = mc;
      sh.fragmentShader = "uniform float uLit;\nuniform float uMix;\nuniform vec3 uMixCol;\n" + sh.fragmentShader
        .replace("#include <map_fragment>", ["#ifdef USE_MAP",
          "  vec4 tc = texture2D( map, vUv );", "  tc.rgb = mix( vec3( 1.0 ), tc.rgb, tc.a );",
          "  diffuseColor.rgb *= mix( tc.rgb, uMixCol, uMix );", "#endif"].join("\n"))
        .replace("#include <output_fragment>", "outgoingLight = mix( outgoingLight, diffuseColor.rgb, uLit );\n#include <output_fragment>");
    };
    m.customProgramCacheKey = function () { return tag; };
    return m;
  }
  var MAT = { front: artMat("ardorSegFront", true), back: artMat("ardorSegBack", true), thru: artMat("ardorSegThru", false, 0.84) };
  function lam(hex) { return new THREE.MeshLambertMaterial({ color: lin(hex) }); }
  function metal(hex, shin, spec) { return new THREE.MeshPhongMaterial({ color: lin(hex), specular: lin(spec || "#4a4f55"), shininess: shin || 50 }); }
  var M = {
    alu: metal("#c4c8cd", 60, "#7d838a"), black: metal("#1b1c1f", 35, "#3f4349"), foot: metal("#9da2a8", 45, "#5b6067"),
    dark: metal("#3a3d42", 30, "#4a4e54"), blackout: lam("#17181b"), keder: lam("#45484d"),
    inner: lam("#d9d9d4"), innerB: lam("#d9d9d4"), led: new THREE.MeshBasicMaterial({ color: lin("#8d9196") })
  };
  M.innerB.side = THREE.BackSide;
  var groundMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, depthWrite: false, toneMapped: false });

  function plainTexture(c) {
    var t = new THREE.CanvasTexture(c);
    t.encoding = THREE.sRGBEncoding;
    t.anisotropy = maxAniso;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.magFilter = THREE.LinearFilter;
    t.minFilter = canMip ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
    t.generateMipmaps = canMip;
    return t;
  }
  var groundCv = doc.createElement("canvas");
  groundCv.width = groundCv.height = 1024;
  var groundTex = plainTexture(groundCv);
  groundMat.map = groundTex;

  var input = {}, state = normalize({ key: "NL-10FT" }), builtKey = "", segRoot = null, builds = 0, geos = [], ground = null,
    wallMesh = null, ptsAll = [], ptsNear = [], cAll = new THREE.Vector3(), target = new THREE.Vector3(0, 40, 0), fitHome = 300,
    size = { w: 0, h: 0 }, lightsOn = opts.lights !== false, shadows = [], glow = [],
    faces = { front: { src: null }, back: { src: null } },
    TEX = { front: { cv: doc.createElement("canvas"), tex: null, ck: "" }, back: { cv: doc.createElement("canvas"), tex: null, ck: "" } };

  /* ---------- textures ---------- */
  // The visible rect is copied out of the print page into the viewer's own canvas, so the edge band and bleed never
  // reach the GPU. rot turns art that sits sideways in the page (wall mount hung the other way) upright.
  function cropInto(T, src, tpl, r, rot, label) {
    var sx = src ? src.width / tpl.w : 0, sy = src ? src.height / tpl.h : 0, k = Math.min(1024 / Math.max(r[2], r[3]), 40),
      w = src ? r[2] * sx : r[2] * k, h = src ? r[3] * sy : r[3] * k, s = Math.min(1, 4096 / Math.max(w, h)),
      cw = Math.max(2, Math.round((rot ? h : w) * s)), ch = Math.max(2, Math.round((rot ? w : h) * s)),
      ck = JSON.stringify([r, tpl, rot, src ? [src.width, src.height] : label]);
    if (T.tex && ck === T.ck && T.src === src && !T.force) return T.tex;
    T.ck = ck; T.src = src; T.force = false;
    if (T.cv.width !== cw || T.cv.height !== ch || !T.tex) {
      T.cv.width = cw; T.cv.height = ch;
      if (T.tex) T.tex.dispose();
      T.tex = plainTexture(T.cv);
    } else T.tex.needsUpdate = true;
    var g = T.cv.getContext("2d");
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, cw, ch);
    if (src) { // 1.5 source pixels in from each side, so the (anti-aliased) boundary pixel never gets in
      var ix = w > 24 ? 1.5 : 0, iy = h > 24 ? 1.5 : 0, aw = rot ? ch : cw, ah = rot ? cw : ch;
      if (rot === 1) g.setTransform(0, -1, 1, 0, 0, ch); // art top at the page's right edge: turn it back
      else if (rot === -1) g.setTransform(0, 1, -1, 0, cw, 0);
      try { g.drawImage(src, r[0] * sx + ix, r[1] * sy + iy, w - 2 * ix, h - 2 * iy, 0, 0, aw, ah); } catch (e) { }
      g.setTransform(1, 0, 0, 1, 0, 0);
    } else {
      var fs = Math.round(Math.min(cw * 0.06, ch * 0.16));
      g.fillStyle = "#ffffff"; g.fillRect(0, 0, cw, ch);
      g.fillStyle = "#a6abb2"; g.textAlign = "center"; g.textBaseline = "middle";
      g.font = "600 " + fs + "px Helvetica, Arial, sans-serif";
      g.fillText(label, cw / 2, ch * 0.45);
    }
    return T.tex;
  }
  function near(a, b) { return Math.abs(a - b) / Math.max(a, b) < 0.03; }
  function setMap(m, t) { if (m.map !== t) { if (!m.map !== !t) m.needsUpdate = true; m.map = t; } }
  function textures(force) {
    var C = state, P = C.P, B = C.B, fs = faces.front.src, bs = faces.back.src, ft, bt = null, r, src, tpl;
    if (force) TEX.front.force = TEX.back.force = true;
    var rot = C.rot, pt = P.tpl, pv = P.vis, as = fs ? fs.width / fs.height : 0;
    if (rot && fs && near(as, pt.h / pt.w) && !near(as, pt.w / pt.h)) { // the designer sent the page as hung: no turning
      rot = 0; pt = { w: pt.h, h: pt.w }; pv = [pv[1], pv[0], pv[3], pv[2]];
    }
    ft = cropInto(TEX.front, fs, pt, C.cat === "fabric" ? [0, 0, pt.w, pt.h] : pv, rot,
      C.cat === "fabric" ? "Your fabric print" : "Your artwork goes here");
    if (C.faces === 2) {
      if (C.cat === "popup") { // the back is the front panel size; "same as front" uses the front panel of the wrap
        src = bs || fs; tpl = P.tpl;
        if (bs && !(near(bs.width / bs.height, P.tpl.w / P.tpl.h) && !near(B.tpl.w / B.tpl.h, P.tpl.w / P.tpl.h))) { tpl = B.tpl; r = B.vis; }
        else r = [B.from[0] + B.vis[0] * B.from[2] / B.tpl.w, B.from[1] + B.vis[1] * B.from[3] / B.tpl.h, B.vis[2] * B.from[2] / B.tpl.w, B.vis[3] * B.from[3] / B.tpl.h];
        bt = cropInto(TEX.back, src, tpl, r, 0, "Your back artwork goes here");
      } else if (bs) bt = cropInto(TEX.back, bs, P.tpl, P.vis, 0, "");
      else bt = ft;
    }
    setMap(MAT.front, ft); setMap(MAT.thru, ft); setMap(MAT.back, bt || ft);
  }

  var controls = new THREE.OrbitControls(camera, canvas);
  controls.enablePan = false;
  controls.enableDamping = true;
  controls.dampingFactor = 0.12;
  controls.rotateSpeed = 0.8;
  controls.zoomSpeed = 0.9;
  controls.minPolarAngle = PI / 2 - EL_MAX;
  controls.maxPolarAngle = PI / 2 - EL_MIN;

  /* ---------- geometry helpers ---------- */
  function addTo(G, geo, mat, x, y, z) {
    var m = new THREE.Mesh(geo, mat);
    m.position.set(x || 0, y || 0, z || 0);
    G.add(m); if (geos.indexOf(geo) < 0) geos.push(geo);
    return m;
  }
  function boxAt(G, mat, w, h, d, x, y, z) { return addTo(G, new THREE.BoxGeometry(w, h, d), mat, x, y, z); }
  function cylAt(G, mat, r, y0, y1, x, z, seg) { return addTo(G, new THREE.CylinderGeometry(r, r, y1 - y0, seg || 16), mat, x, (y0 + y1) / 2, z); }
  function rod(G, mat, r, a, b) { // a thin cylinder from point a to point b
    var va = new THREE.Vector3(a[0], a[1], a[2]), vb = new THREE.Vector3(b[0], b[1], b[2]),
      m = addTo(G, new THREE.CylinderGeometry(r, r, va.distanceTo(vb), 8), mat);
    m.position.copy(va).add(vb).multiplyScalar(0.5);
    m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), vb.sub(va).normalize());
    return m;
  }
  // a print plane w x h centred at (x, y, z); back = faces -z and reads correctly from behind; mirror = seen through the fabric
  function sheet(G, mat, w, h, x, y, z, back, mirror) {
    var g = new THREE.PlaneGeometry(w, h), uv = g.attributes.uv, i;
    if (back) g.rotateY(PI);
    if (mirror) for (i = 0; i < uv.count; i++) uv.setX(i, 1 - uv.getX(i));
    return addTo(G, g, mat, x, y, z);
  }
  function disposeGeos() { for (var i = 0; i < geos.length; i++) try { geos[i].dispose(); } catch (e) { } geos.length = 0; }
  // a flat foot: a bar with round ends, l long (along z), w wide, h tall
  function foot(G, x, z, l, w, h) {
    boxAt(G, M.foot, w, h, l - w, x, h / 2, z);
    cylAt(G, M.foot, w / 2, 0, h, x, z - (l - w) / 2, 14);
    cylAt(G, M.foot, w / 2, 0, h, x, z + (l - w) / 2, 14);
    shadows.push(["r", x - w / 2, z - l / 2, x + w / 2, z + l / 2, 0.42, 1.2]);
  }
  // a rectangular extrusion frame (outer W x H, bar width b, depth d) with its bottom at y0, centred on z
  function frame(G, mat, W, H, b, d, y0, z) {
    boxAt(G, mat, W, b, d, 0, y0 + H - b / 2, z);
    boxAt(G, mat, W, b, d, 0, y0 + b / 2, z);
    boxAt(G, mat, b, H - 2 * b, d, -W / 2 + b / 2, y0 + H / 2, z);
    boxAt(G, mat, b, H - 2 * b, d, W / 2 - b / 2, y0 + H / 2, z);
  }

  /* ---------- models ---------- */
  // freestanding displays and the backlit lightbox: an aluminium extrusion frame on 2 feet (3 on the 20 ft), the graphic
  // stretched over the front (and the back when double sided). Single sided: the back shows the frame and the fabric's
  // reverse (non-lit) or the blackout fabric (backlit).
  function buildFrame(G) {
    var C = state, m = C.m, lit = C.cat === "backlit", vw = C.dw, vh = C.dh, rim = m.rim, W = vw + 2 * rim, H = vh + 2 * rim,
      y0 = m.footH, d = m.prof, b = Math.min(m.bar, W / 6), fm = /^#?(0|1|2)/.test(m.color) ? M.black : M.alu,
      cy = y0 + H / 2, zf = d / 2 + 0.08, i, n, x;
    frame(G, fm, W, H, b, d, y0, 0);
    sheet(G, MAT.front, vw, vh, 0, cy, zf);
    if (C.faces === 2) sheet(G, MAT.back, vw, vh, 0, cy, -zf, true);
    else if (lit) sheet(G, M.blackout, vw, vh, 0, cy, -zf, true);
    else {
      sheet(G, MAT.thru, vw, vh, 0, cy, d / 2 - 0.02, true, true);
      n = m.single ? 0 : Math.max(0, Math.round(vw / m.bay) - 1);
      if (m.centre && n % 2 === 0) n++;
      for (i = 1; i <= n; i++) { x = -W / 2 + b / 2 + (W - b) * i / (n + 1); boxAt(G, fm, b * 0.8, H - 2 * b, d * 0.85, x, cy, -0.05 * d); }
    }
    var nf = m.feet !== null ? Math.max(2, Math.round(m.feet)) : 2, fx = W / 2 - m.footIn;
    for (i = 0; i < nf; i++) foot(G, nf < 2 ? 0 : -fx + 2 * fx * i / (nf - 1), 0, Math.max(m.footL, d + 2), m.footW, y0);
    shadows.push(["r", -W / 2, -d / 2 - 0.5, W / 2, d / 2 + 0.5, 0.22, 5], ["r", -W / 2 - 6, -m.footL / 2, W / 2 + 6, m.footL / 2, 0.08, 18]);
    if (lit) { glow.push([0, d / 2, W, 1]); if (C.faces === 2) glow.push([0, -d / 2, W, -1]); }
  }
  // the popup: one wrap graphic around the front and both returns (rounded corners), the back panel on its own
  // (blackout fabric when single sided), an open top that shows the accordion frame and the LED bars
  function buildPopup(G) {
    var C = state, m = C.m, P = C.P, W = C.dw, H = C.dh, D = C.dd, r = Math.min(m.radius, D / 4), y0 = m.lift, y1 = y0 + H,
      hw = W / 2, hd = D / 2, rb = 0.4, LA = D - r - rb, arc = PI * r / 2, LF = W - 2 * r, v = P.vis, x1 = P.folds[0], x2 = P.folds[1],
      sA = LA + arc / 2, sB = LA + arc + LF + arc / 2, sT = 2 * LA + 2 * arc + LF, pts = [], i, a, s;
    function pt(x, z, nx, nz) { pts.push([x, z, nx, nz]); }
    pt(-hw, -hd + rb, -1, 0);
    for (i = 0; i <= 8; i++) { a = PI - PI / 2 * i / 8; pt(-hw + r + r * Math.cos(a), hd - r + r * Math.sin(a), Math.cos(a), Math.sin(a)); }
    for (i = 0; i <= 8; i++) { a = PI / 2 - PI / 2 * i / 8; pt(hw - r + r * Math.cos(a), hd - r + r * Math.sin(a), Math.cos(a), Math.sin(a)); }
    pt(hw, -hd + rb, 1, 0);
    // page x of a point s along the wrap: the folds land at the middle of the corner arcs
    function pageX(s) {
      return s <= sA ? v[0] + (x1 - v[0]) * s / sA : s <= sB ? x1 + (x2 - x1) * (s - sA) / (sB - sA) : x2 + (v[0] + v[2] - x2) * (s - sB) / (sT - sB);
    }
    var Pp = [], N = [], T = [], I = [], g;
    for (i = 0, s = 0; i < pts.length; i++) {
      if (i) s += Math.sqrt(Math.pow(pts[i][0] - pts[i - 1][0], 2) + Math.pow(pts[i][1] - pts[i - 1][1], 2));
      var u = (pageX(Math.min(s, sT)) - v[0]) / v[2];
      Pp.push(pts[i][0], y0, pts[i][1], pts[i][0], y1, pts[i][1]);
      N.push(pts[i][2], 0, pts[i][3], pts[i][2], 0, pts[i][3]);
      T.push(u, 0, u, 1);
      if (i) I.push(2 * i - 2, 2 * i, 2 * i - 1, 2 * i - 1, 2 * i, 2 * i + 1);
    }
    g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(Pp, 3));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(N, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(T, 2));
    g.setIndex(I);
    addTo(G, g, MAT.front); addTo(G, g, M.innerB); // the inside of the fabric, seen over the open top
    var bwid = W - 2 * rb, cy = (y0 + y1) / 2;
    sheet(G, C.faces === 2 ? MAT.back : M.blackout, bwid, H, 0, cy, -hd, true);
    sheet(G, M.inner, bwid, H, 0, cy, -hd + 0.02);
    for (i = -1; i <= 1; i += 2) boxAt(G, M.dark, 0.9, H, 0.9, i * (hw - 0.45), cy, -hd + 0.45); // back corner channels
    // accordion frame behind the front and back fabric: X rods per bay, channel bars on top, LED bars
    var bays = Math.max(2, Math.round(W / 30)), zf = hd - 2.2, zb = -hd + 2.2, xs, xe;
    for (i = 0; i < bays; i++) {
      xs = -hw + 2 + (W - 4) * i / bays; xe = -hw + 2 + (W - 4) * (i + 1) / bays;
      rod(G, M.dark, 0.22, [xs, y0 + 1.5, zf], [xe, y1 - 1.5, zf]); rod(G, M.dark, 0.22, [xs, y1 - 1.5, zf], [xe, y0 + 1.5, zf]);
      rod(G, M.dark, 0.22, [xs, y0 + 1.5, zb], [xe, y1 - 1.5, zb]); rod(G, M.dark, 0.22, [xs, y1 - 1.5, zb], [xe, y0 + 1.5, zb]);
    }
    boxAt(G, M.alu, W - 2 * r, 0.5, 0.8, 0, y1 - 0.25, hd - 0.5); boxAt(G, M.alu, bwid, 0.5, 0.8, 0, y1 - 0.25, -hd + 0.5);
    boxAt(G, M.alu, 0.8, 0.5, D - 2, -hw + 0.5, y1 - 0.25, 0); boxAt(G, M.alu, 0.8, 0.5, D - 2, hw - 0.5, y1 - 0.25, 0);
    for (i = -1; i <= 1; i += 2) {
      boxAt(G, M.led, W - 8, 0.5, 1.1, 0, y1 - 1.6, i * (hd - 3.2));
      boxAt(G, M.led, W - 8, 0.5, 1.1, 0, y0 + 1.6, i * (hd - 3.2));
    }
    var fx = [-hw + 2, hw - 2];
    if (W > 100) fx.push(0);
    for (i = 0; i < fx.length; i++) { cylAt(G, M.dark, 0.9, 0, y0, fx[i], hd - 2); cylAt(G, M.dark, 0.9, 0, y0, fx[i], -hd + 2); }
    shadows.push(["r", -hw, -hd, hw, hd, 0.3, 3], ["r", -hw - 4, -hd - 4, hw + 4, hd + 4, 0.1, 14]);
    glow.push([0, hd, W, 1]);
    if (C.faces === 2) glow.push([0, -hd, W, -1]);
  }
  // the wall mount: a thin black frame (1 in deep) on a wall, hung portrait or landscape
  function buildWall(G) {
    var C = state, m = C.m, vw = C.dw, vh = C.dh, W = vw + 2 * m.rim, H = vh + 2 * m.rim, d = m.depth,
      cy = Math.max(m.hang, H / 2 + 14), fm = /^#?(0|1|2|3)/.test(m.color) ? M.black : M.alu;
    frame(G, fm, W, H, Math.min(1, W / 8), d, cy - H / 2, d / 2);
    sheet(G, MAT.front, vw, vh, 0, cy, d + 0.06);
    sheet(G, M.blackout, vw, vh, 0, cy, 0.02, true);
    var big = Math.max(W, H) * 3 + 60, wc = doc.createElement("canvas"), g, N = 512, k = N / big, rnd = rng(7), i;
    wc.width = wc.height = N; g = wc.getContext("2d");
    g.fillStyle = "#efedea"; g.fillRect(0, 0, N, N);
    for (i = 0; i < 3000; i++) { g.fillStyle = rnd() > 0.5 ? "rgba(255,255,255,0.4)" : "rgba(90,88,84,0.08)"; g.fillRect(rnd() * N, rnd() * N, 1.3, 1.3); }
    g.save(); g.shadowColor = "rgba(20,20,18,0.42)"; g.shadowBlur = Math.max(2, 2.2 * k); g.shadowOffsetX = 4096; g.shadowOffsetY = 1.2 * k;
    g.fillStyle = "#000"; g.fillRect(N / 2 - W / 2 * k - 4096, N / 2 - H / 2 * k, W * k, H * k); g.restore();
    var fade = g.createRadialGradient(N / 2, N / 2, 0, N / 2, N / 2, N / 2);
    fade.addColorStop(0, "rgba(0,0,0,1)"); fade.addColorStop(0.55, "rgba(0,0,0,1)"); fade.addColorStop(0.97, "rgba(0,0,0,0)");
    g.globalCompositeOperation = "destination-in"; g.fillStyle = fade; g.fillRect(0, 0, N, N);
    var wt = plainTexture(wc), wm = new THREE.MeshBasicMaterial({ map: wt, transparent: true, depthWrite: false, toneMapped: false });
    wallMesh = addTo(G, new THREE.PlaneGeometry(big, big), wm, 0, cy, -m.gap);
    wallMesh.renderOrder = -1;
    wallMesh.userData.own = [wm, wt];
  }
  // custom fabric: the printed fabric only, no frame. The silicone edge (a thin rubber fin) is sewn round the back.
  function buildFabric(G) {
    var C = state, m = C.m, W = C.dw, H = C.dh, t = m.thick, y0 = m.lift, cy = y0 + H / 2, kd = m.keder, kt = m.kederT, z = -t / 2 - kd / 2;
    sheet(G, MAT.front, W, H, 0, cy, t / 2);
    sheet(G, MAT.thru, W, H, 0, cy, -t / 2, true, true);
    boxAt(G, M.keder, W, kt, kd, 0, y0 + H - kt / 2, z); boxAt(G, M.keder, W, kt, kd, 0, y0 + kt / 2, z);
    boxAt(G, M.keder, kt, H - 2 * kt, kd, -W / 2 + kt / 2, cy, z); boxAt(G, M.keder, kt, H - 2 * kt, kd, W / 2 - kt / 2, cy, z);
    shadows.push(["r", -W / 2, -0.6, W / 2, 0.6, 0.25, 2.5], ["r", -W / 2 - 3, -6, W / 2 + 3, 6, 0.07, 14]);
    if (C.lit) glow.push([0, t / 2, W, 1]);
  }

  /* ---------- the floor: a soft disc with baked contact shadows, fading into the background ---------- */
  function paintShadows(g, X0, Z0, s, list) {
    var OF = 4096, i, e;
    for (i = 0; i < list.length; i++) {
      e = list[i];
      g.save(); g.shadowColor = "rgba(24,24,20," + e[5] + ")"; g.shadowBlur = Math.max(1, e[6] * s); g.shadowOffsetX = OF;
      g.fillStyle = "#000"; g.beginPath();
      g.rect((e[1] - X0) * s - OF, (e[2] - Z0) * s, (e[3] - e[1]) * s, (e[4] - e[2]) * s);
      g.fill(); g.restore();
    }
  }
  function paintGround(box) {
    if (state.cat === "wall") { if (ground) ground.visible = false; return; }
    var N = groundCv.width, g = groundCv.getContext("2d"), cx = (box.min.x + box.max.x) / 2, cz = (box.min.z + box.max.z) / 2,
      R = Math.max(box.max.x - box.min.x, box.max.z - box.min.z) * 0.62 + 30, s = N / (2 * R), rnd = rng(11), night = lightsOn && state.lit, i, e;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.globalCompositeOperation = "source-over";
    g.clearRect(0, 0, N, N);
    g.fillStyle = night ? "#34373c" : "#dad8d3"; g.fillRect(0, 0, N, N);
    for (i = 0; i < 7000; i++) {
      g.fillStyle = rnd() > 0.5 ? (night ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.35)") : "rgba(90,88,84,0.14)";
      g.fillRect(rnd() * N, rnd() * N, 1 + rnd() * 1.5, 1 + rnd() * 1.5);
    }
    if (night) for (i = 0; i < glow.length; i++) { // light spilling onto the floor in front of a lit face
      e = glow[i];
      var gx = (e[0] - cx + R) * s, gz = (e[1] - cz + R) * s, rx = e[2] * 0.62 * s, rz = Math.min(e[2] * 0.3, 48) * s, gr;
      g.save(); g.translate(gx, gz); g.scale(1, rz / rx);
      gr = g.createRadialGradient(0, e[3] * rx * 0.08, 0, 0, e[3] * rx * 0.08, rx);
      gr.addColorStop(0, "rgba(255,247,226,0.5)"); gr.addColorStop(0.45, "rgba(255,247,226,0.2)"); gr.addColorStop(1, "rgba(255,247,226,0)");
      g.fillStyle = gr; g.fillRect(-rx, e[3] > 0 ? 0 : -rx, 2 * rx, rx); g.restore();
    }
    paintShadows(g, cx - R, cz - R, s, shadows);
    var fade = g.createRadialGradient(N / 2, N / 2, 0, N / 2, N / 2, N / 2);
    fade.addColorStop(0, "rgba(0,0,0,1)"); fade.addColorStop(0.5, "rgba(0,0,0,1)"); fade.addColorStop(0.76, "rgba(0,0,0,0.5)");
    fade.addColorStop(0.96, "rgba(0,0,0,0)"); fade.addColorStop(1, "rgba(0,0,0,0)");
    g.globalCompositeOperation = "destination-in"; g.fillStyle = fade; g.fillRect(0, 0, N, N);
    g.globalCompositeOperation = "source-over";
    groundTex.needsUpdate = true;
    if (!ground) {
      ground = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), groundMat);
      ground.rotation.x = -PI / 2; ground.renderOrder = -1;
      scene.add(ground);
    }
    ground.visible = true;
    ground.scale.set(R, R, 1);
    ground.position.set(cx, 0.02, cz);
    ground.userData.box = box;
  }

  /* ---------- the little overlay: lights switch (backlit) and the fabric-only note ---------- */
  var ui = null, uiBtn = null, uiNote = null;
  if (opts.ui !== false) {
    ui = doc.createElement("div");
    ui.setAttribute("data-ardor-seg3d-ui", "");
    ui.style.cssText = "position:absolute;top:10px;" + (opts.uiCorner === "right" ? "right" : "left") + ":10px;z-index:3;display:flex;gap:6px;pointer-events:none;";
    var pill = "pointer-events:auto;font:600 12px/1 Helvetica,Arial,sans-serif;padding:7px 11px;border-radius:999px;border:1px solid rgba(0,0,0,.14);background:rgba(255,255,255,.94);color:#24272b;";
    uiBtn = doc.createElement("button");
    uiBtn.type = "button"; uiBtn.style.cssText = pill + "cursor:pointer;";
    uiBtn.addEventListener("click", function (e) { e.preventDefault(); api.lights(!lightsOn); });
    uiNote = doc.createElement("span");
    uiNote.style.cssText = pill;
    uiNote.textContent = "Fabric only - no frame included";
    ui.appendChild(uiBtn); ui.appendChild(uiNote);
    host.appendChild(ui);
  }
  // sit just below anything the page already shows in that top corner (e.g. the designer's chips)
  function placeUi() {
    if (!ui || !ui.parentNode) return;
    try {
      var hr = host.getBoundingClientRect(), par = host.parentNode, top = 10, right = opts.uiCorner === "right", i, el, r, cs;
      for (i = 0; par && i < par.children.length; i++) {
        el = par.children[i];
        if (el === host || el.hidden) continue;
        cs = win.getComputedStyle(el);
        if (cs.position !== "absolute" || cs.display === "none" || cs.visibility === "hidden") continue;
        r = el.getBoundingClientRect();
        if (r.width < 1 || r.height < 1 || r.top - hr.top > 90 || r.bottom <= hr.top) continue;
        if (right ? r.right < hr.right - 200 : r.left > hr.left + 200) continue;
        top = Math.max(top, Math.round(r.bottom - hr.top + 8));
      }
      ui.style.top = top + "px";
    } catch (e) { }
  }
  function syncUi() {
    if (!ui) return;
    placeUi();
    var vw = host.closest ? host.closest("[data-abb-viewer]") : null, own = null; // the designer has its own lights button
    try { own = (vw || host.parentNode || doc).querySelector("[data-abb-lights]"); } catch (e) { own = null; }
    uiBtn.style.display = state.lit && !own ? "" : "none";
    uiBtn.textContent = lightsOn ? "Lights: on" : "Lights: off";
    uiBtn.setAttribute("aria-pressed", lightsOn ? "true" : "false");
    uiBtn.setAttribute("aria-label", lightsOn ? "Turn the display lights off" : "Turn the display lights on");
    uiNote.style.display = state.cat === "fabric" && opts.fabricNote !== false ? "" : "none";
  }
  // room light: lights on dims the room so the backlit graphic glows
  var lastNight = null;
  function applyLights() {
    var night = lightsOn && state.lit, i;
    LIT.value = night ? 1 : 0;
    renderer.setClearColor(night ? bgNight : bgDay, 1);
    for (i = 0; i < LAMPS.length; i++) LAMPS[i][0].intensity = LAMPS[i][1] * (night ? 0.5 : 1);
    M.led.color.copy(lin(night ? "#fffaf0" : "#8d9196"));
    M.inner.emissive.copy(night ? lin("#bdbab2") : new THREE.Color(0));
    M.innerB.emissive.copy(M.inner.emissive);
    if (night !== lastNight && ground && ground.userData.box) paintGround(ground.userData.box);
    lastNight = night;
    syncUi();
  }

  function buildAll() {
    if (segRoot) {
      scene.remove(segRoot);
      if (wallMesh) { wallMesh.userData.own[0].dispose(); wallMesh.userData.own[1].dispose(); wallMesh = null; }
      disposeGeos();
    }
    builds++;
    segRoot = new THREE.Group();
    scene.add(segRoot);
    shadows = []; glow = [];
    var c = state.cat;
    if (c === "popup") buildPopup(segRoot);
    else if (c === "wall") buildWall(segRoot);
    else if (c === "fabric") buildFabric(segRoot);
    else buildFrame(segRoot);
    segRoot.updateMatrixWorld(true);
    var box = new THREE.Box3(), i;
    for (i = 0; i < segRoot.children.length; i++) if (segRoot.children[i] !== wallMesh) box.expandByObject(segRoot.children[i]);
    paintGround(box); lastNight = lightsOn && state.lit;
    var X0 = box.min.x, X1 = box.max.x, Z0 = box.min.z, Z1 = box.max.z, Y0 = Math.max(0, box.min.y), Y1 = box.max.y + 0.3,
      mg = c === "wall" ? Math.max(3, 0.1 * Math.max(Y1 - Y0, X1 - X0)) : Math.max(0.6, 0.03 * Math.max(Y1, X1 - X0));
    X0 -= mg; X1 += mg; Z0 -= mg; Z1 += mg;
    if (c === "wall") { Y0 = Math.max(0, Y0 - mg); Y1 += mg; } else Y0 = 0;
    ptsAll = []; ptsNear = [];
    cAll.set((X0 + X1) / 2, (Y0 + Y1) / 2, (Z0 + Z1) / 2);
    for (i = 0; i < 8; i++) {
      ptsAll.push(new THREE.Vector3(i & 1 ? X1 : X0, i & 2 ? Y1 : Y0, i & 4 ? Z1 : Z0));
      ptsNear.push(new THREE.Vector3(cAll.x + (i & 1 ? 1 : -1) * (X1 - X0) * 0.17, cAll.y + (i & 2 ? 1 : -1) * (Y1 - Y0) * 0.17, cAll.z + (i & 4 ? 1 : -1) * (Z1 - Z0) * 0.17));
    }
    var big = Math.max(Y1, X1 - X0, Z1 - Z0);
    camera.near = Math.max(0.25, big / 150); camera.far = big * 30;
    camera.updateProjectionMatrix();
    controls.minAzimuthAngle = c === "wall" ? -1.3 : -Infinity;
    controls.maxAzimuthAngle = c === "wall" ? 1.3 : Infinity;
    applyLights();
  }

  /* ---------- camera ---------- */
  var raf = 0, tween = null, backupT = 0;
  function now() { return win.performance && win.performance.now ? win.performance.now() : Date.now(); }
  function invalidate() {
    if (disposed) return;
    if (!raf) raf = win.requestAnimationFrame(frameTick);
    // a background tab does not run animation frames: draw anyway a little later
    if (!backupT) backupT = win.setTimeout(function () { backupT = 0; if (raf) { win.cancelAnimationFrame(raf); raf = 0; frameTick(); } }, 250);
  }
  function draw() { try { renderer.render(scene, camera); } catch (e) { } }
  function frameTick() {
    raf = 0;
    if (disposed || (!size.w && !measure())) return;
    var moving = !!tween;
    if (tween) stepTween();
    controls.update();
    holdInView();
    draw();
    if (tween || moving) invalidate();
  }
  function getSph() {
    var ox = camera.position.x - target.x, oy = camera.position.y - target.y, oz = camera.position.z - target.z,
      r = Math.sqrt(ox * ox + oy * oy + oz * oz) || 1;
    return { az: Math.atan2(ox, oz), el: Math.asin(Math.max(-1, Math.min(1, oy / r))), r: r };
  }
  function setSph(az, el, r) {
    var ce = Math.cos(el);
    camera.position.set(target.x + r * ce * Math.sin(az), target.y + r * Math.sin(el), target.z + r * ce * Math.cos(az));
    camera.lookAt(target);
  }
  function fitDistance(az, el, aspect, share, pts, c) {
    var tv = Math.tan(FOV * PI / 360), th = tv * (aspect || 1), ce = Math.cos(el), se = Math.sin(el), sa = Math.sin(az),
      ca = Math.cos(az), m = 1 / (share || 1), best = 1, i, x, y, z, cx, cy, cz, d;
    for (i = 0; i < pts.length; i++) {
      x = pts[i].x - c.x; y = pts[i].y - c.y; z = pts[i].z - c.z;
      cx = x * ca - z * sa; cy = -x * se * sa + y * ce - z * se * ca; cz = x * ce * sa + y * se + z * ce * ca;
      d = cz + Math.max(Math.abs(cx) * m / th, Math.abs(cy) * m / tv);
      if (d > best) best = d;
    }
    return best;
  }
  function aspectNow() { return size.w > 0 && size.h > 0 ? size.w / size.h : 1.3; }
  function farFor(az, el, asp) { return fitDistance(az, el, asp || aspectNow(), FILL_HOME, ptsAll, cAll); }
  function nearFor(az, el, asp) { return Math.min(farFor(az, el, asp) * 0.9, fitDistance(az, el, asp || aspectNow(), FILL_NEAR, ptsNear, cAll)); }
  function place(az, el, r) {
    target.copy(cAll); controls.target.copy(target);
    setSph(az, el, r);
  }
  function viewOf(name) {
    var v = typeof name === "string" && VIEWS.hasOwnProperty(name) ? VIEWS[name] : VIEWS.home, az = v[0];
    if (state.cat === "wall") az = name === "back" ? 0 : Math.max(-1.2, Math.min(1.2, az)); // nothing to see behind a wall
    return { az: az, el: v[1] };
  }
  function updateLimits() {
    var s = getSph(), h = viewOf("home");
    fitHome = farFor(h.az, h.el);
    controls.minDistance = nearFor(s.az, s.el);
    controls.maxDistance = Math.max(fitDistance(0, 0.1, aspectNow(), FILL_FAR, ptsAll, cAll), fitHome * 1.3);
  }
  function clampR(r, az, el) { return Math.max(nearFor(az, el), Math.min(controls.maxDistance, r)); }
  function holdInView() {
    var s = getSph(), nr = nearFor(s.az, s.el);
    controls.minDistance = nr;
    if (!tween && s.r < nr - 0.01) setSph(s.az, s.el, nr);
  }
  function stepTween() {
    var t = (now() - tween.t0) / tween.ms;
    if (t >= 1) t = 1;
    var k = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    place(tween.a.az + (tween.b.az - tween.a.az) * k, tween.a.el + (tween.b.el - tween.a.el) * k, tween.a.r + (tween.b.r - tween.a.r) * k);
    if (t >= 1) tween = null;
  }
  var reduced = false;
  try { reduced = !!(win.matchMedia && win.matchMedia("(prefers-reduced-motion: reduce)").matches); } catch (e5) { reduced = false; }
  function tweenTo(az, el, r, ms) {
    var a = getSph(), d = az - a.az;
    while (d > PI) d -= PI * 2;
    while (d < -PI) d += PI * 2;
    tween = { a: a, b: { az: a.az + d, el: el, r: r }, t0: now(), ms: reduced ? 1 : ms };
    invalidate();
  }
  function measure() {
    var w = host.clientWidth | 0, h = host.clientHeight | 0;
    if (w < 2 || h < 2) return false;
    var pr = Math.min(2, Math.max(1, win.devicePixelRatio || 1));
    if (w === size.w && h === size.h && pr === renderer.getPixelRatio()) return false;
    var oldFit = fitHome, had = size.w > 0;
    size.w = w; size.h = h;
    renderer.setPixelRatio(pr); renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();
    updateLimits();
    var s = getSph();
    if (had && !tween) place(s.az, s.el, clampR(s.r * fitHome / oldFit, s.az, s.el));
    else if (had && tween) tween.b.r = tween.b.r * fitHome / oldFit;
    else { var hv = viewOf("home"); place(hv.az, hv.el, fitHome); }
    controls.update();
    return true;
  }
  function onControlsChange() { invalidate(); }
  function onControlsStart() { tween = null; canvas.style.cursor = "grabbing"; }
  function onControlsEnd() { canvas.style.cursor = "grab"; }
  controls.addEventListener("change", onControlsChange);
  controls.addEventListener("start", onControlsStart);
  controls.addEventListener("end", onControlsEnd);

  // set() keeps these; a new key (or family) forgets the old display's fields that are not passed again
  var FIELDS = ["key", "family", "cat", "name", "sign", "display", "front", "back", "hardware", "hw", "tpl", "W", "H", "visible", "vis",
    "hidden", "folds", "fromFront", "faces", "sides", "size", "custom", "cw", "ch", "graphic", "orient", "orientation", "turn", "order"];
  var api = {
    set: function (o) {
      if (disposed || !o || typeof o !== "object") return;
      var k, oldFit = fitHome, s = getSph(), first = !builtKey,
        newKey = o.key !== undefined && keyOf(o.key) !== keyOf(input.key);
      if (newKey || (o.family !== undefined && str(o.family) !== str(input.family)))
        for (k = newKey ? 1 : 2; k < FIELDS.length - 1; k++) if (o[FIELDS[k]] === undefined) delete input[FIELDS[k]];
      for (k = 0; k < FIELDS.length; k++) if (o[FIELDS[k]] !== undefined) input[FIELDS[k]] = o[FIELDS[k]];
      if (o.lights !== undefined) lightsOn = o.lights !== false;
      state = normalize(input);
      var gk = geoKey(state);
      if (gk !== builtKey) {
        buildAll(); builtKey = gk;
        updateLimits();
        if (first) { var hv = viewOf("home"); place(hv.az, hv.el, fitHome); }
        else {
          if (tween) tween.b.r = tween.b.r * fitHome / oldFit;
          var a = state.cat === "wall" ? Math.max(-1.2, Math.min(1.2, s.az)) : s.az;
          place(a, s.el, clampR(s.r * fitHome / oldFit, a, s.el));
        }
        controls.update();
      } else applyLights();
      textures(false);
      invalidate();
    },
    // face: "front" | "back"; cv: the whole print page as printed (null = none)
    setFace: function (face, cv) {
      if (disposed || !faces.hasOwnProperty(face)) return;
      faces[face].src = cv && cv.width > 0 && cv.height > 0 ? cv : null;
      textures(true); invalidate();
    },
    refresh: function () { // after redrawing a canvas already passed (a new size is fine too)
      if (disposed) return;
      textures(true);
      invalidate();
    },
    view: function (name) {
      if (disposed) return;
      var v = viewOf(name);
      tweenTo(v.az, v.el, farFor(v.az, v.el), 560);
    },
    zoom: function (dir) {
      if (disposed) return;
      var s = tween ? tween.b : getSph(), r = clampR(s.r * Math.pow(0.8, dir > 0 ? 1 : -1), s.az, s.el);
      tweenTo(s.az, s.el, r, 240);
    },
    resize: function () {
      if (disposed) return;
      placeUi();
      if (measure()) { if (raf) { win.cancelAnimationFrame(raf); raf = 0; } draw(); invalidate(); }
    },
    // backlit displays and popups: lights on (default) or off; no argument = read
    lights: function (on) {
      if (disposed) return false;
      if (on === undefined) return lightsOn;
      lightsOn = on !== false;
      applyLights(); invalidate();
      try { host.dispatchEvent(new win.CustomEvent("ardorseg3d:lights", { bubbles: true, detail: { on: lightsOn } })); } catch (e) { }
      return lightsOn;
    },
    lightsOn: function () { return lightsOn; },
    snapshot: function (w, h, viewName) {
      if (disposed) return null;
      var out = null, oldPR = renderer.getPixelRatio(), oldSize = renderer.getSize(new THREE.Vector2()), oldAspect = camera.aspect,
        oldPos = camera.position.clone(), oldTarget = target.clone(), s = getSph();
      try {
        var cap = Math.min(4096, renderer.capabilities.maxTextureSize || 4096);
        w = Math.max(16, Math.min(cap, Math.round(w || size.w || 800)));
        h = Math.max(16, Math.min(cap, Math.round(h || size.h || 600)));
        renderer.setPixelRatio(1); renderer.setSize(w, h, false);
        camera.aspect = w / h; camera.updateProjectionMatrix();
        if (typeof viewName === "string" && VIEWS.hasOwnProperty(viewName)) {
          var v = viewOf(viewName);
          place(v.az, v.el, farFor(v.az, v.el, w / h));
        } else {
          place(s.az, s.el, s.r * farFor(s.az, s.el, w / h) / farFor(s.az, s.el, oldAspect || 1));
        }
        renderer.render(scene, camera);
        out = doc.createElement("canvas"); out.width = w; out.height = h;
        var ctx = out.getContext("2d"), night = lightsOn && state.lit;
        ctx.fillStyle = "#" + (night ? bgNight : bgDay).getHexString(); ctx.fillRect(0, 0, w, h); ctx.drawImage(canvas, 0, 0, w, h);
        if (state.cat === "fabric" && opts.fabricNote !== false) { // keep the "fabric only" note on the proof picture
          var fs = Math.max(12, Math.round(w / 40)), tx = "Fabric only - no frame included";
          ctx.font = "600 " + fs + "px Helvetica, Arial, sans-serif";
          var tw = ctx.measureText(tx).width, px = fs * 0.8, bh = fs * 2;
          ctx.fillStyle = "rgba(255,255,255,0.94)"; ctx.strokeStyle = "rgba(0,0,0,0.16)"; ctx.lineWidth = 1;
          ctx.beginPath(); ctx.rect(fs, fs, tw + 2 * px, bh); ctx.fill(); ctx.stroke();
          ctx.fillStyle = "#24272b"; ctx.textBaseline = "middle"; ctx.fillText(tx, fs + px, fs + bh / 2);
        }
      } catch (e) { out = null; }
      try {
        renderer.setPixelRatio(oldPR); renderer.setSize(oldSize.x, oldSize.y, false);
        camera.aspect = oldAspect; camera.updateProjectionMatrix();
        target.copy(oldTarget); controls.target.copy(target); camera.position.copy(oldPos); camera.lookAt(target);
        renderer.render(scene, camera);
      } catch (e2) { }
      return out;
    },
    // extra (not in the contract): small debug numbers for testing
    stats: function () {
      if (disposed) return null;
      var mm = renderer.info.memory, r = renderer.info.render, C = state;
      return { geometries: mm.geometries, textures: mm.textures, frame: r.frame, calls: r.calls, triangles: r.triangles,
        cat: C.cat, key: C.key, order: C.order, faces: C.faces, visible: C.P.vis.slice(), page: [C.P.tpl.w, C.P.tpl.h],
        folds: C.P.folds.slice(), back: C.B ? { page: [C.B.tpl.w, C.B.tpl.h], visible: C.B.vis.slice(), from: C.B.from } : null,
        display: [C.dw, C.dh, C.dd], rot: C.rot, lit: C.lit, lights: lightsOn && C.lit, builds: builds,
        azimuth: getSph().az, distance: getSph().r };
    },
    dispose: function () {
      if (disposed) return;
      disposed = true;
      if (raf) win.cancelAnimationFrame(raf);
      if (backupT) win.clearTimeout(backupT);
      raf = 0; backupT = 0; tween = null;
      try { if (ro) ro.disconnect(); } catch (e) { }
      win.removeEventListener("resize", onWinResize);
      doc.removeEventListener("visibilitychange", onVis);
      canvas.removeEventListener("webglcontextlost", onLost, false);
      canvas.removeEventListener("webglcontextrestored", onRestored, false);
      controls.removeEventListener("change", onControlsChange);
      controls.removeEventListener("start", onControlsStart);
      controls.removeEventListener("end", onControlsEnd);
      controls.dispose();
      if (wallMesh) { wallMesh.userData.own[0].dispose(); wallMesh.userData.own[1].dispose(); wallMesh = null; }
      disposeGeos();
      if (ground) ground.geometry.dispose();
      var list = [groundMat, groundTex], k;
      for (k in M) if (M.hasOwnProperty(k)) list.push(M[k]);
      for (k in MAT) if (MAT.hasOwnProperty(k)) list.push(MAT[k]);
      for (k in TEX) if (TEX.hasOwnProperty(k)) { list.push(TEX[k].tex); TEX[k].tex = TEX[k].src = null; TEX[k].cv.width = 1; }
      faces.front.src = faces.back.src = null;
      for (k = 0; k < list.length; k++) try { if (list[k]) list[k].dispose(); } catch (e3) { }
      try { renderer.dispose(); renderer.forceContextLoss(); } catch (e4) { }
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      if (ui && ui.parentNode) ui.parentNode.removeChild(ui);
    }
  };
  function onWinResize() { api.resize(); }
  function onLost(e) { e.preventDefault(); }
  function onRestored() { invalidate(); }
  function onVis() { if (!doc.hidden) invalidate(); }
  canvas.addEventListener("webglcontextlost", onLost, false);
  canvas.addEventListener("webglcontextrestored", onRestored, false);
  doc.addEventListener("visibilitychange", onVis);
  var ro = null;
  if (win.ResizeObserver) { ro = new win.ResizeObserver(function () { api.resize(); }); ro.observe(host); }
  win.addEventListener("resize", onWinResize);

  api.set({ key: "NL-10FT" });
  if (!measure()) controls.update();
  invalidate();
  canvas.setAttribute("data-ardor-seg3d", "ready");
  return api;
}

root.ArdorSeg3D = { create: create, version: VERSION, load: function (o) { return loadLibs(libUrls(o || {})); } };
})(typeof window !== "undefined" ? window : this);
